import React, { useState, useEffect } from "react";
import {
  getConfig, updateConfig, getLicenseSkus,
  getDepartments, getLocations, createLocation, updateLocation, deleteLocation, setDepartmentLocation,
} from "../services/api";
import ProgressBar from "../components/ProgressBar";

const TEMPLATE_FIELDS = [
  { key: "office_location", label: "Büro" },
  { key: "street_address",  label: "Straße" },
  { key: "postal_code",     label: "Postleitzahl" },
  { key: "city",            label: "Ort" },
  { key: "state",           label: "Bundesland oder Provinz" },
  { key: "country",         label: "Land oder Region" },
  { key: "phone",           label: "Telefon (geschäftlich)" },
  { key: "fax",             label: "Faxnummer" },
];

const EMPTY_TEMPLATE = TEMPLATE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

function locationSummary(location) {
  if (!location) return "";
  return [location.street_address, location.postal_code, location.city].filter(Boolean).join(", ");
}

function LocationCard({ location, isNew, onSaved, onDeleted, onCancelNew }) {
  const [name, setName] = useState(location?.name || "");
  const [values, setValues] = useState({ ...EMPTY_TEMPLATE, ...location });
  const [open, setOpen] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (key, value) => {
    setSaved(false);
    setValues((v) => ({ ...v, [key]: value }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Bitte einen Namen für den Standort angeben.");
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const payload = { ...values, name: name.trim() };
      const res = isNew
        ? await createLocation(payload)
        : await updateLocation(location.id, payload);
      onSaved(res.data.location, isNew);
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Standort „${location.name}“ wirklich löschen? Abteilungen, die diesen Standort verwenden, verlieren die Zuweisung.`)) {
      return;
    }
    setDeleting(true);
    try {
      await deleteLocation(location.id);
      onDeleted(location.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, marginBottom: 10, overflow: "hidden" }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", cursor: "pointer", background: "#f8f9fa",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>{name || "(neuer Standort)"}</span>
          {!isNew && (
            <span style={{ fontSize: 11, color: "#666" }}>{locationSummary(location) || "keine Adresse hinterlegt"}</span>
          )}
        </div>
        <span style={{ fontSize: 13, color: "#0078d4" }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{ padding: "14px" }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#999", marginBottom: 3 }}>Name des Standorts</div>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setSaved(false); }}
              placeholder="z. B. Hauptsitz, Filiale Nord, …"
              style={{ width: "100%", maxWidth: 320, padding: "6px 8px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
            {TEMPLATE_FIELDS.map((f) => (
              <div key={f.key}>
                <div style={{ fontSize: 11, color: "#999", marginBottom: 3 }}>{f.label}</div>
                <input
                  type="text"
                  value={values[f.key] || ""}
                  onChange={(e) => handleChange(f.key, e.target.value)}
                  style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
            ))}
          </div>

          {error && <div style={{ fontSize: 12, color: "#d13438", marginTop: 10 }}>{error}</div>}

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              style={{ padding: "7px 18px", borderRadius: 6, border: "none", background: "#0078d4", color: "white", fontWeight: 600, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1, fontSize: 13 }}
            >
              {saving ? "Speichern…" : "Speichern"}
            </button>
            {saved && <span style={{ fontSize: 13, color: "#107c10" }}>✓ Gespeichert</span>}

            {isNew ? (
              <button
                onClick={onCancelNew}
                style={{ padding: "7px 18px", borderRadius: 6, border: "1px solid #e1e4e8", background: "white", color: "#666", fontWeight: 600, cursor: "pointer", fontSize: 13, marginLeft: "auto" }}
              >
                Abbrechen
              </button>
            ) : (
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ padding: "7px 18px", borderRadius: 6, border: "1px solid #f0c0c0", background: "white", color: "#d13438", fontWeight: 600, cursor: deleting ? "default" : "pointer", fontSize: 13, marginLeft: "auto", opacity: deleting ? 0.7 : 1 }}
              >
                {deleting ? "Löschen…" : "Standort löschen"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DepartmentLocationRow({ department, locationId, locations, onChanged }) {
  const [saving, setSaving] = useState(false);
  const location = locations.find((l) => l.id === locationId);

  const handleChange = async (e) => {
    const value = e.target.value ? Number(e.target.value) : null;
    setSaving(true);
    try {
      await setDepartmentLocation(department, value);
      onChanged(department, value);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f0f0", gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{department}</div>
        {location ? (
          <div style={{ fontSize: 12, color: "#666" }}>{locationSummary(location) || "keine Adresse hinterlegt"}</div>
        ) : (
          <div style={{ fontSize: 12, color: "#d13438" }}>⚠️ kein Standort zugewiesen</div>
        )}
      </div>
      <select
        value={locationId || ""}
        onChange={handleChange}
        disabled={saving}
        style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, minWidth: 220 }}
      >
        <option value="">— kein Standort —</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </select>
    </div>
  );
}

export default function SettingsPage() {
  const [config, setConfig] = useState(null);
  const [skus, setSkus] = useState([]);
  const [skuError, setSkuError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Drucker-Einstellungen
  const [printerIp,   setPrinterIp]   = useState("");
  const [printerUser, setPrinterUser] = useState("");
  const [printerPass, setPrinterPass] = useState("");
  const [printerSaving, setPrinterSaving] = useState(false);
  const [printerSaved,  setPrinterSaved]  = useState(false);

  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]); // [{id, name, ...adressfelder}]
  const [departmentLocations, setDepartmentLocations] = useState({}); // department -> location_id
  const [addingLocation, setAddingLocation] = useState(false);

  useEffect(() => {
    Promise.all([
      getConfig(),
      getLicenseSkus().catch((err) => {
        setSkuError(err.response?.data?.error || err.message);
        return { data: { skus: [] } };
      }),
      getDepartments().catch(() => ({ data: { departments: [] } })),
      getLocations().catch(() => ({ data: { locations: [], departmentLocations: {} } })),
    ])
      .then(([configRes, skuRes, deptRes, locRes]) => {
        setConfig(configRes.data);
        setSkus(skuRes.data.skus || []);
        try {
          const parsed = JSON.parse(configRes.data.default_license_skus || "[]");
          setSelected(Array.isArray(parsed) ? parsed : []);
        } catch {
          setSelected([]);
        }

        setLocations(locRes.data.locations || []);
        setDepartmentLocations(locRes.data.departmentLocations || {});
        setDepartments([...(deptRes.data.departments || [])].sort((a, b) => a.localeCompare(b, "de")));

        // Drucker-Konfiguration aus config laden
        setPrinterIp(configRes.data.printer_ip || "");
        setPrinterUser(configRes.data.printer_username || "");
        // Passwort nicht vorausfüllen (Sicherheit)
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLocationSaved = (location, wasNew) => {
    setLocations((prev) => {
      const next = wasNew ? [...prev, location] : prev.map((l) => (l.id === location.id ? location : l));
      return [...next].sort((a, b) => a.name.localeCompare(b.name, "de"));
    });
    if (wasNew) setAddingLocation(false);
  };

  const handleLocationDeleted = (id) => {
    setLocations((prev) => prev.filter((l) => l.id !== id));
    setDepartmentLocations((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((dept) => {
        if (next[dept] === id) delete next[dept];
      });
      return next;
    });
  };

  const handleDepartmentLocationChanged = (department, locationId) => {
    setDepartmentLocations((prev) => {
      const next = { ...prev };
      if (locationId) next[department] = locationId;
      else delete next[department];
      return next;
    });
  };

  const toggleSku = (skuId) => {
    setSaved(false);
    setSelected((prev) =>
      prev.includes(skuId) ? prev.filter((id) => id !== skuId) : [...prev, skuId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await updateConfig({ default_license_skus: JSON.stringify(selected) });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const handlePrinterSave = async () => {
    setPrinterSaving(true);
    setPrinterSaved(false);
    try {
      const payload = { printer_ip: printerIp.trim(), printer_username: printerUser.trim() };
      if (printerPass) payload.printer_password = printerPass;
      await updateConfig(payload);
      setPrinterSaved(true);
      setPrinterPass(""); // Passwort-Feld leeren nach Speichern
    } finally {
      setPrinterSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, maxWidth: 400, margin: "0 auto" }}><ProgressBar label="Lade Einstellungen…" /></div>;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Einstellungen</h1>
        <p style={{ color: "#666", marginTop: 4 }}>Lizenzverwaltung &amp; Synchronisations-Optionen</p>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 6 }}>Standard-Lizenzen für neue Konten</h2>
        <p style={{ fontSize: 13, color: "#666", marginTop: 0, marginBottom: 16 }}>
          Diese Lizenzen werden automatisch zugewiesen, wenn beim Sync ein neues Azure-AD-Konto
          angelegt wird. Beim Deaktivieren eines Kontos (Offboarding) werden alle zugewiesenen
          Lizenzen automatisch wieder entfernt – das passiert bereits ohne weitere Einstellung.
        </p>

        {skuError && (
          <div style={{ padding: 12, background: "#fde7e9", color: "#d13438", borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
            ❌ Lizenzen konnten nicht geladen werden: {skuError}
            <div style={{ marginTop: 6, color: "#a4262c" }}>
              Hinweis: Hierfür muss der App-Registrierung in Entra ID die Berechtigung
              <code style={{ margin: "0 4px" }}>Organization.Read.All</code>
              (Application, mit Admin-Zustimmung) erteilt sein.
            </div>
          </div>
        )}

        {!skuError && skus.length === 0 && (
          <p style={{ fontSize: 13, color: "#999" }}>Keine Lizenzen im Tenant gefunden.</p>
        )}

        {skus.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {skus.map((sku) => (
              <label key={sku.skuId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, border: "1px solid #f0f0f0", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={selected.includes(sku.skuId)}
                  onChange={() => toggleSku(sku.skuId)}
                  style={{ width: 16, height: 16 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{sku.friendlyName}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>{sku.skuPartNumber}</div>
                </div>
                <div style={{ fontSize: 12, color: sku.available > 0 ? "#107c10" : "#d13438" }}>
                  {sku.available} / {sku.enabled} frei
                </div>
              </label>
            ))}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={handleSave}
            disabled={saving || !!skuError}
            className="btn-primary"
            style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "#0078d4", color: "white", fontWeight: 600, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>
          {saved && <span style={{ fontSize: 13, color: "#107c10" }}>✓ Gespeichert</span>}
        </div>
      </div>

      <div className="card" style={{ maxWidth: 900, marginTop: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6, gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Standorte</h2>
            <p style={{ fontSize: 13, color: "#666", marginTop: 6, marginBottom: 0 }}>
              Lege hier eigenständige Standorte mit Adress- und Kontaktdaten an. Diese können
              anschließend einer oder mehreren Abteilungen zugewiesen werden – Abteilungen
              am selben Standort verwenden so automatisch dieselben Daten.
            </p>
          </div>
          {!addingLocation && (
            <button
              onClick={() => setAddingLocation(true)}
              className="btn-primary"
              style={{ padding: "7px 16px", borderRadius: 6, border: "none", background: "#0078d4", color: "white", fontWeight: 600, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" }}
            >
              + Standort hinzufügen
            </button>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          {addingLocation && (
            <LocationCard
              location={null}
              isNew
              onSaved={handleLocationSaved}
              onCancelNew={() => setAddingLocation(false)}
            />
          )}

          {locations.length === 0 && !addingLocation ? (
            <p style={{ fontSize: 13, color: "#999" }}>Noch keine Standorte angelegt.</p>
          ) : (
            locations.map((loc) => (
              <LocationCard
                key={loc.id}
                location={loc}
                isNew={false}
                onSaved={handleLocationSaved}
                onDeleted={handleLocationDeleted}
              />
            ))
          )}
        </div>
      </div>

      <div className="card" style={{ maxWidth: 900, marginTop: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 6 }}>Abteilungen</h2>
        <p style={{ fontSize: 13, color: "#666", marginTop: 0, marginBottom: 16 }}>
          Jeder Abteilung kann hier ein Standort zugewiesen werden. Die Adress- und
          Kontaktdaten des Standorts werden beim Anlegen neuer Azure-AD-Konten automatisch
          in die Profilinformationen (Büro, Adresse, Telefon, Fax) übernommen. Mehrere
          Abteilungen am selben Ort können denselben Standort verwenden.
        </p>

        {departments.length === 0 ? (
          <p style={{ fontSize: 13, color: "#999" }}>
            Keine Abteilungen gefunden – bitte zuerst HRLab-Daten laden (Synchronisation → Schritt 1).
          </p>
        ) : (
          <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden" }}>
            {departments.map((dept) => (
              <DepartmentLocationRow
                key={dept}
                department={dept}
                locationId={departmentLocations[dept] || null}
                locations={locations}
                onChanged={handleDepartmentLocationChanged}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Drucker-Einstellungen ── */}
      <div className="card" style={{ maxWidth: 700, marginTop: 20 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 6 }}>🖨️ Drucker (Canon iR-ADV C3826i)</h2>
        <p style={{ fontSize: 13, color: "#666", marginTop: 0, marginBottom: 16 }}>
          Verbindungsdaten für die Remote UI des Druckers. Wird für den automatischen
          Adressbuch-Sync unter <strong>Drucker</strong> benötigt.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: "#666", fontWeight: 600, marginBottom: 4 }}>IP-Adresse des Druckers</div>
            <input
              type="text"
              value={printerIp}
              onChange={(e) => { setPrinterIp(e.target.value); setPrinterSaved(false); }}
              placeholder="192.168.1.100"
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, boxSizing: "border-box" }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#666", fontWeight: 600, marginBottom: 4 }}>Benutzername (leer = kein Login)</div>
            <input
              type="text"
              value={printerUser}
              onChange={(e) => { setPrinterUser(e.target.value); setPrinterSaved(false); }}
              placeholder="administrator"
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ fontSize: 12, color: "#666", fontWeight: 600, marginBottom: 4 }}>
              Passwort / PIN &nbsp;<span style={{ fontWeight: 400, color: "#999" }}>(nur ausfüllen wenn ändern)</span>
            </div>
            <input
              type="password"
              value={printerPass}
              onChange={(e) => { setPrinterPass(e.target.value); setPrinterSaved(false); }}
              placeholder="••••••••"
              style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e1e4e8", fontSize: 13, boxSizing: "border-box" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={handlePrinterSave}
            disabled={printerSaving}
            style={{ padding: "8px 20px", borderRadius: 6, border: "none", background: "#0078d4", color: "white", fontWeight: 600, cursor: printerSaving ? "default" : "pointer", opacity: printerSaving ? 0.7 : 1, fontSize: 13 }}
          >
            {printerSaving ? "Speichern…" : "Speichern"}
          </button>
          {printerSaved && <span style={{ fontSize: 13, color: "#107c10" }}>✓ Gespeichert</span>}
        </div>

        <p style={{ fontSize: 12, color: "#999", marginTop: 12, marginBottom: 0 }}>
          💡 Verbindung testen und Adressbuch übertragen unter{" "}
          <strong>🖨️ Drucker → Verbindung testen</strong>.
        </p>
      </div>
    </div>
  );
}
